import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/cn";

/**
 * 우하단 점의 색. 도메인 용어가 아니라 **표현 용어**로 둔다.
 * 메일의 PeerStatus를 여기로 옮기는 건 features 쪽 책임이다.
 * (react-component-rules §2 — 공용 컴포넌트는 도메인을 모른다)
 */
export type AvatarDotTone = "positive" | "pending" | "neutral" | "negative";

const avatar = cva(
  "relative flex shrink-0 items-center justify-center font-semibold text-white select-none",
  {
    variants: {
      size: {
        xs: "size-6.5 text-[11px]",
        sm: "size-8.5 text-xs",
        md: "size-9 text-sm",
        lg: "size-10 text-[15px]",
        xl: "size-12 text-[17px]",
        "2xl": "size-16 text-2xl",
        "3xl": "size-21 text-[32px]",
      },
      shape: {
        rounded: "",
        circle: "rounded-full",
      },
    },
    compoundVariants: [
      { shape: "rounded", size: "xs", class: "rounded-[9px]" },
      { shape: "rounded", size: "sm", class: "rounded-xl" },
      { shape: "rounded", size: "md", class: "rounded-avatar-sm" },
      { shape: "rounded", size: "lg", class: "rounded-[14px]" },
      { shape: "rounded", size: "xl", class: "rounded-avatar" },
      { shape: "rounded", size: "2xl", class: "rounded-[22px]" },
      { shape: "rounded", size: "3xl", class: "rounded-avatar-lg" },
    ],
    defaultVariants: { size: "xl", shape: "rounded" },
  },
);

const dot = cva("absolute right-[-2px] bottom-[-2px] rounded-full box-border", {
  variants: {
    tone: {
      positive: "bg-status-active",
      pending: "bg-status-awaiting",
      neutral: "bg-status-idle",
      negative: "bg-status-noreply",
    },
    size: {
      xs: "size-2.75 border-2",
      sm: "size-3 border-2",
      md: "size-3.25 border-[2.5px]",
      lg: "size-3.25 border-[2.5px]",
      xl: "size-3.75 border-[2.5px]",
      "2xl": "size-4.5 border-[3px]",
      "3xl": "size-5 border-[3px]",
    },
    /** 점의 테두리는 아바타가 놓인 배경색과 같아야 파여 보인다. */
    surface: {
      light: "border-surface",
      dark: "border-ink",
    },
  },
  defaultVariants: { size: "xl", surface: "light" },
});

type AvatarProps = VariantProps<typeof avatar> & {
  /** 표시할 이니셜. 계산은 호출부(@catmail/shared의 initialsFor)에서 한다. */
  initials: string;
  /** 팔레트 인덱스(0부터). 주소 해시로 정해지므로 여기서 고르지 않는다. */
  colorIndex: number;
  imageUrl?: string | null;
  dotTone?: AvatarDotTone | null;
  /** 점 테두리 색을 놓인 배경에 맞춘다. */
  surface?: "light" | "dark";
  /** 접근성용 이름. 시각적으로는 이니셜만 보이므로 별도로 받는다. */
  label: string;
} & Omit<ComponentPropsWithoutRef<"span">, "children">;

export function Avatar({
  initials,
  colorIndex,
  imageUrl,
  dotTone,
  surface = "light",
  size,
  shape,
  label,
  className,
  ...rest
}: AvatarProps) {
  // 클래스 이름을 문자열로 조립하면 Tailwind가 스캔하지 못한다.
  // 토큰을 CSS 변수로 넘긴다. (docs/tailwind-rules.md §6①)
  const background = `var(--color-avatar-${(colorIndex % 11) + 1})`;

  return (
    <span
      className={cn(avatar({ size, shape }), className)}
      style={{ backgroundColor: background }}
      role="img"
      aria-label={label}
      {...rest}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className={cn(
            "size-full object-cover",
            shape === "circle" ? "rounded-full" : "rounded-[inherit]",
          )}
        />
      ) : (
        // 두 글자짜리 이니셜은 그대로 두면 넘치므로 한 단계 줄인다
        <span className={initials.length >= 2 ? "text-[0.86em]" : undefined}>
          {initials}
        </span>
      )}

      {dotTone ? (
        <span className={dot({ tone: dotTone, size, surface })}>
          {/* 발신전용 표시. 색만으로 상태를 나타내지 않기 위한 가로줄 */}
          {dotTone === "negative" ? (
            <span className="absolute top-1/2 left-1/2 h-[2px] w-[6px] -translate-x-1/2 -translate-y-1/2 rounded-[1px] bg-white" />
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
